import React, { useState }  from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Switch,
    Avatar,
    IconButton,
    Grid,
    Paper,
    InputAdornment
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import {createUserAction,clearCreateUserAction,updateTenantUserAction,clearUpdateTenantUserAction, getUserByIdAction, clearGetUserByIdAction} from "../../store/actions";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useEffect } from "react";


const UserInformation = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [showPassword, setShowPassword] = useState(false);
    const [formPopulated, setFormPopulated] = useState(false);

    // Listen for create user result
    const { successData: createData, error: createError, errorInfo: createErrorInfo } = useSelector((state) => state.createUserReducer);
    // Listen for update user result
    const { successData: updateData, error: updateError, errorInfo: updateErrorInfo } = useSelector((state) => state.updateTenantUserReducer || {});
    // Listen for get user by id result
    const { successData: userByIdData } = useSelector((state) => state.getUserByIdReducer || {});

    // Get userId and mode from navigation state
    const userId = location.state?.userId;
    const mode = location.state?.mode;


    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const formik = useFormik({
        initialValues: {
            fullName: "",
            mobile: "",
            email: "",
            role: "",
            password: "",
            locationId: "",
            status: true,
            joinedDate: ""
        },
        validationSchema: Yup.object({
            fullName: Yup.string().required("Full name is required"),
            mobile: Yup.string()
                .required("Mobile number is required")
                .matches(/^\d{10}$/, "Mobile number must be exactly 10 digits"),
            email: Yup.string().email("Invalid email address"),
            role: Yup.string().required("Role is required"),
            password: mode === 'edit' ? Yup.string() : Yup.string().min(8, "Password must be at least 8 characters").required("Password is required"),
            locationId: Yup.string(),
            status: Yup.boolean(),
            joinedDate: Yup.string()
        }),
        onSubmit: (values, { setSubmitting, resetForm }) => {
            // Build base payload
            let payload = {
                role: values.role,
                name: values.fullName,
                mobile: values.mobile,
                joinedDate: values.joinedDate || undefined,
                email: values.email || undefined,
                locationId: values.locationId || undefined,
                status: values.status ? 'ACTIVE' : 'EXITED'
            };
            if (mode === 'edit' && userId) {
                // For edit, add password if provided
                if (values.password && values.password.trim() !== "") {
                    payload = { ...payload, password: values.password };
                }
                dispatch(updateTenantUserAction({ ...payload, endPoint: `tenant-user/${userId}` }));
            } else {
                // For create, add password
                dispatch(createUserAction({ ...payload, password: values.password }));
            }
            setSubmitting(false);
        }
    });

        // Fetch user by id if in edit mode
    useEffect(() => {
        if (mode === 'edit' && userId) {
            dispatch(clearGetUserByIdAction());
            setFormPopulated(false);
            dispatch(getUserByIdAction({ endPoint: `tenant-user/${userId}` }));
        }
        
    }, [dispatch, mode, userId]);

    // Populate form when user data arrives
    useEffect(() => {
        if (mode === 'edit' && userByIdData && userByIdData.data && !formPopulated) {
            const u = userByIdData.data;
            formik.setValues({
                fullName: u.name || '',
                mobile: u.mobile || '',
                email: u.email || '',
                role: u.role || '',
                password: '', // Do not prefill password
                locationId: u.locationId || '',
                status: u.status === 'ACTIVE', // false for 'EXITED'
                joinedDate: u.joinedDate || ''
            });
            setFormPopulated(true);
        }
    }, [userByIdData, mode, formPopulated]);

    useEffect(() => {
        if (mode === 'edit') {
            if (updateData) {
                toast.success("User updated successfully!");
                formik.resetForm();
                dispatch(clearUpdateTenantUserAction());
                navigate('/masterData/user');
            }
            if (updateError) {
                toast.error(updateErrorInfo || "Failed to update user");
                dispatch(clearUpdateTenantUserAction());
            }
        } else {
            if (createData) {
                toast.success("User created successfully!");
                formik.resetForm();
                dispatch(clearCreateUserAction());
                navigate('/masterData/user');
            }
            if (createError) {
                toast.error(createErrorInfo || "Failed to create user");
                dispatch(clearCreateUserAction());
            }
        }
    }, [createData, createError, createErrorInfo, updateData, updateError, updateErrorInfo, dispatch, navigate, mode]);


    // Role options
    const roleOptions = [
         "AE", "ZM", "RM", "AM", "SO", "MDO"
    ];

    return (
        <Paper
            elevation={0}
            sx={{
                mx: "auto",
                p: 2,
                mt:{ xs: 0, sm: 6, md: 0 },
                borderRadius: 3
            }}
        >
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h5" fontWeight={600}>
                        User Information
                    </Typography>
                    <Typography color="text.secondary">
                        Create and manage system user profiles
                    </Typography>
                </Box>

                <IconButton onClick={() => navigate(-1)} aria-label="close">
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Profile Photo + Status */}
            <Box
                mt={4}
                p={3}
                border="1px solid #E5E7EB"
                borderRadius={3}
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent={{ xs: "flex-start", sm: "space-between" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                gap={{ xs: 2, sm: 0 }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    flexDirection={{ xs: "column", sm: "row" }}
                >
                    <Avatar
                        sx={{ width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, bgcolor: "#E0E7FF" }}
                    />
                    <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                        <Typography fontWeight={600}>Profile Photo</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Upload a photo for your profile (Max 2MB)
                        </Typography>
                    </Box>
                </Box>

                <Button
                    variant="outlined"
                    sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" }, mt: { xs: 2, sm: 0 } }}
                >
                    Upload
                </Button>
            </Box>

            <Box mt={3} display="flex" justifyContent="space-between">
                <Box>
                    <Typography fontWeight={600}>Account Status</Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                    <Switch
                        checked={formik.values.status}
                        onChange={(e) =>
                            formik.setFieldValue("status", e.target.checked)
                        }
                    />
                    <Typography fontWeight={500}>{formik.values.status ? 'Active' : 'Exited'}</Typography>
                </Box>
            </Box>

            {/* Form Fields */}
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3} mt={1}>
                    <Grid item size={{ xs: 12, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                            Full Name
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Enter full name"
                            name="fullName"
                            value={formik.values.fullName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.fullName && Boolean(formik.errors.fullName)}
                            helperText={formik.touched.fullName && formik.errors.fullName}
                        />

                    </Grid>

                    <Grid item size={{ xs: 12, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                            Mobile Number
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Enter mobile number"
                            name="mobile"
                            value={formik.values.mobile}
                            onChange={e => {
                                // Only allow digits and max 10
                                const val = e.target.value.replace(/[^\d]/g, '').slice(0, 10);
                                formik.setFieldValue('mobile', val);
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                            helperText={formik.touched.mobile && formik.errors.mobile}
                            inputProps={{ maxLength: 10 }}
                        />

                    </Grid>

                    <Grid item size={{ xs: 12, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                            Email Address
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Enter email address"
                            name="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                        />

                    </Grid>

                    <Grid item size={{ xs: 12, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                            Role
                        </Typography>
                        <Autocomplete
                            options={roleOptions}
                            value={formik.values.role || null}
                            onChange={(_, value) => formik.setFieldValue("role", value || "")}
                            onBlur={formik.handleBlur}
                            getOptionLabel={(option) => option === "AE" ? "Admin Exec" : option}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    fullWidth
                                    placeholder="Select role"
                                    name="role"
                                    error={formik.touched.role && Boolean(formik.errors.role)}
                                    helperText={formik.touched.role && formik.errors.role}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option === value}
                            autoHighlight
                            clearOnEscape
                        />

                    </Grid>

                    <Grid item size={{ xs: 12, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                            Password
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Enter password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={formik.touched.password && formik.errors.password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                     <Grid item size={{ xs: 12, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                            Joined Date
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Enter joined date"
                            name="joinedDate"
                            type="date"
                            value={formik.values.joinedDate}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.joinedDate && Boolean(formik.errors.joinedDate)}
                            helperText={formik.touched.joinedDate && formik.errors.joinedDate}

                        />
                    </Grid>
                </Grid>

                {/* Footer Buttons */}
                <Box
                    mt={5}
                    display="flex"
                    justifyContent="flex-end"
                    gap={2}
                >
                    <Button
                        variant="outlined"
                        sx={{ textTransform: "none", px: 4 }}
                        type="reset"
                        onClick={() => {
                            formik.resetForm();
                            navigate('/masterData/user');
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        type="submit"
                        variant="contained"
                        sx={{
                            textTransform: "none",
                            px: 5,
                            bgcolor: "#4F46E5",
                            borderRadius: 2
                        }}
                        disabled={formik.isSubmitting}
                    >
                        {mode === 'edit' ? 'Update User' : 'Save User'}
                    </Button>
               </Box>
         </form>
         </Paper>
    );
};

export default UserInformation;
